import { motion } from "framer-motion";
import { TrendingUp, Percent, Calendar, Heart } from "lucide-react";

const BenefitsSection = () => {
  const benefits = [
    {
      icon: TrendingUp,
      title: "Increase Repeat Customers",
      description: "Loyalty members visit 2-3x more often than non-members. Turn occasional visitors into regulars.",
      metric: "2.7x",
      metricLabel: "more visits",
      color: "indigo",
    },
    {
      icon: Percent,
      title: "Reduce Discount Dependency",
      description: "Stop training customers to wait for sales. Rewards create value without cutting your margins.",
      metric: "40%",
      metricLabel: "less discounting",
      color: "emerald",
    },
    {
      icon: Calendar,
      title: "Predictable Revenue",
      description: "Know when customers will return. Points expiring soon? They'll come back. Reward unlocked? They'll visit.",
      metric: "85%",
      metricLabel: "revenue predictability",
      color: "violet",
    },
    {
      icon: Heart,
      title: "Build Real Relationships",
      description: "Move beyond transactions. Customers feel valued and connected to your brand.",
      metric: "94%",
      metricLabel: "customer satisfaction",
      color: "amber",
    },
  ];

  const colorClasses = {
    indigo: {
      bg: "bg-indigo-100",
      icon: "text-indigo-600",
      metric: "text-indigo-600",
    },
    emerald: {
      bg: "bg-emerald-100",
      icon: "text-emerald-600",
      metric: "text-emerald-600",
    },
    violet: {
      bg: "bg-violet-100",
      icon: "text-violet-600",
      metric: "text-violet-600",
    },
    amber: {
      bg: "bg-amber-100",
      icon: "text-amber-600",
      metric: "text-amber-600",
    },
  };

  return (
    <section 
      className="section-padding bg-slate-50"
      data-testid="benefits-section"
    >
      <div className="container-main">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium mb-4 border border-emerald-200">
            Real Results
          </span>
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="benefits-headline"
          >
            Benefits That Actually Matter
          </h2>
          <p className="text-lg text-slate-600">
            Not just vanity metrics. Real business outcomes that impact your bottom line.
          </p>
        </motion.div>

        {/* Benefits grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300"
              data-testid={`benefit-${benefit.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-start gap-6">
                <div className={`w-16 h-16 rounded-2xl ${colorClasses[benefit.color].bg} flex items-center justify-center flex-shrink-0`}>
                  <benefit.icon className={`w-8 h-8 ${colorClasses[benefit.color].icon}`} />
                </div>
                <div className="flex-1">
                  <h3 
                    className="text-xl font-bold text-slate-900 mb-2"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {benefit.title}
                  </h3>
                  <p className="text-slate-600 mb-4 leading-relaxed">
                    {benefit.description}
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span 
                      className={`text-4xl font-bold ${colorClasses[benefit.color].metric}`}
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {benefit.metric}
                    </span>
                    <span className="text-sm text-slate-500">{benefit.metricLabel}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Testimonial preview */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center gap-4 px-6 py-4 bg-white rounded-2xl shadow-sm border border-slate-100">
            <div className="flex -space-x-3">
              {[1, 2, 3, 4].map((i) => (
                <div 
                  key={i} 
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-violet-400 border-2 border-white flex items-center justify-center"
                >
                  <span className="text-xs font-medium text-white">{['S', 'D', 'M', 'L'][i-1]}</span>
                </div>
              ))}
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-slate-900">Join 500+ businesses</p>
              <p className="text-xs text-slate-500">Already building loyalty</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BenefitsSection;
